import React from 'react'
import { useParams } from 'react-router-dom'

import { ProvenanceTabContent } from '../components/certificateTabs/tabs/ProvenanceTabContent'
import { CertificateSubView } from './CertificateSubView'

/**
 * VERA-315 Phase 4 — the Provenance tab, as its own screen.
 *
 * The content component is unchanged; only what wraps it moved. `isShowProvenance`
 * is `true` because this screen is owner-only (the row that reaches it renders in
 * the owner view, and the shell re-checks), and for an owner that flag already
 * resolved true when this was a tab — it is `isCertificateOwner || <public flag>`.
 *
 * `isInPreviewMode` is false for a stronger reason: previewing swaps the whole
 * page for the public stack, which has no tabs and now no rows, so the tab
 * version of this prop could never be true either. It was dead.
 *
 * ## Why `isShowOwnership` is false
 *
 * It rendered a "Current owner" card at the top of this screen — avatar, name,
 * email, owner-since date. This screen is owner-only, so that card told you your
 * own name and address; the only part carrying information was the date, and the
 * date is now a fact row (`Owned since`) on the certificate itself, where it is
 * visible without a tap.
 *
 * VERA-299 had already removed this same block from the Product surface for the
 * same reason ("on their own certificate the answer is always you") and kept it
 * here. This finishes that thought. The block still renders for PUBLIC viewers,
 * on the public stack, where the owner's identity is genuinely new information.
 *
 * ## Why the certificate is threaded through rather than re-fetched
 *
 * VERA-304 made `certificate` a required prop of `ProvenanceTabContent` (it feeds
 * `Provenance` and the owner card) instead of a second `useGetCertificatePublic`
 * inside the content. The shell above has already loaded it and hands it to this
 * render prop, so passing it down is one fetch for the screen — re-fetching here
 * would be the same request twice for one render.
 */
export const ProvenanceSubView = () => {
  const { certificateId } = useParams()

  return (
    <CertificateSubView title='Provenance'>
      {certificate => (
        <ProvenanceTabContent
          certificateId={certificateId || ''}
          certificate={certificate}
          isShowProvenance
          isShowOwnership={false}
          isInPreviewMode={false}
        />
      )}
    </CertificateSubView>
  )
}
